import { differenceInMonths, isValid } from 'date-fns';

import { MachineServices } from 'common/bento/types/MachineConfig';
import { apolloClient } from 'common/graphql/clients';
import { TrackSegmentEventDocument } from 'features/Analytics/graphql/mutations/trackSegmentEvent.gql';
import { OnboardingCompanyDocument } from 'features/Onboarding/graphql/queries/onboardingCompany.gql';
import onboardingHubMicroExperiment from 'features/Onboarding/lib/onboardingHubMicroExperiment';

import companyProfileStorage from '../companyProfileStorage';
import { ProvideCompanyLegalInfoDocument } from '../graphql/mutations/provideCompanyLegalInfo.gql';
import { AfterCompanyCreationDocument } from '../graphql/queries/afterCompanyCreation.gql';
import { BusinessInfoModuleDataQuery } from '../graphql/queries/businessInfoModuleData.gql';
import { CompanyProfileInputState, Model } from './model';

const trackOnboardingHubMicroExperimentStarted = ({
  businessInfoData,
  companyAgeInMonths,
  companyProfile,
}: {
  businessInfoData: BusinessInfoModuleDataQuery;
  companyAgeInMonths: number | null;
  companyProfile: Pick<
    CompanyProfileInputState,
    'activityType' | 'isMicro' | 'legalForm'
  >;
}) => {
  const {
    activityType: companyActivityCode,
    isMicro,
    legalForm,
  } = companyProfile;

  if (!isMicro) {
    return;
  }

  const { companyUsers, profile } = businessInfoData.viewer;

  apolloClient.mutate({
    mutation: TrackSegmentEventDocument,
    variables: {
      input: {
        event: 'Onboarding Hub Micro Experiment Started',
        properties: {
          companyActivityCode,
          companyAgeInMonths,
          isInExperiment:
            onboardingHubMicroExperiment.getIsUserInExperiment(profile),
          isMultiAccount: companyUsers.length > 1,
          legalForm,
        },
      },
    },
  });
};

export enum Service {
  SubmitCompanyInfo = 'submitCompanyInfo',
}

const services: MachineServices<Model, Service> = {
  submitCompanyInfo: async ({ companyProfile, getModuleData }) => {
    const { businessInfoData, companyContext, existingCompanyProfileId } =
      getModuleData();
    const {
      activityDetails,
      activityType,
      city,
      inseeLegalFormCode,
      isMicro,
      legalForm,
      legalName,
      legalRegistrationNumber,
      registrationDate,
      street,
      streetAddition,
      tradeName,
      zip,
    } = companyProfile;

    if (!inseeLegalFormCode || !registrationDate) {
      throw new Error('missing company information');
    }

    const companyAgeInMonths =
      registrationDate && isValid(new Date(registrationDate))
        ? differenceInMonths(new Date(), new Date(registrationDate))
        : null;

    apolloClient.mutate({
      mutation: TrackSegmentEventDocument,
      variables: {
        input: {
          event: 'Company Information Submitted',
          properties: {
            companyActivityCode: activityType,
            companyAgeInMonths,
            isMicro,
            legalForm,
          },
        },
      },
    });

    const res = await apolloClient.mutate({
      mutation: ProvideCompanyLegalInfoDocument,
      variables: {
        input: {
          activity: {
            classificationCode: activityType,
            details: activityDetails,
          },
          address: {
            city,
            street,
            streetAddition,
            zip,
          },
          id: existingCompanyProfileId,
          inseeLegalFormCode,
          isMicro,
          legalName,
          legalRegistrationNumber,
          registrationDate,
          tradeName,
        },
      },
      /**
       * If a companyProfileId already exists before company creation, the
       * OnboardingCompany query has already been executed, so we want to refetch
       * it here to prevent stale company data in the rest of the onboarding.
       */
      ...(existingCompanyProfileId
        ? {
            awaitRefetchQueries: true,
            refetchQueries: [
              {
                query: OnboardingCompanyDocument,
                variables: {
                  companyProfileId: existingCompanyProfileId,
                },
              },
            ],
          }
        : []),
    });

    const newlyCreatedCompanyProfileId =
      res.data?.provideCompanyLegalInfo.onboardingCompanyProfile
        ?.companyProfileId;

    if (!newlyCreatedCompanyProfileId) {
      throw new Error('The newly created companyProfileId must be defined');
    }

    trackOnboardingHubMicroExperimentStarted({
      businessInfoData,
      companyAgeInMonths,
      companyProfile,
    });

    // Used to refresh Apollo cache
    await apolloClient.query({
      fetchPolicy: 'network-only',
      query: AfterCompanyCreationDocument,
      variables: {
        companyProfileId: newlyCreatedCompanyProfileId,
      },
    });

    companyContext.setCompanyContext({
      companyProfileId: newlyCreatedCompanyProfileId,
      companyUserId: undefined,
    });

    companyProfileStorage.clear();
  },
};

export default services;
